import type { Article } from './articleTypes';

export const comparerDeuxOccasions: Article = {
  slug: 'comparer-deux-voitures-occasion',
  category: 'Occasion',
  title: 'Comment départager deux voitures d’occasion sans choisir au hasard',
  seoTitle: 'Comparer deux voitures d’occasion : méthode et tableau',
  description:
    'Une méthode en 6 étapes pour comparer deux occasions : historique, version, état, coût global, usage et risque. Avec tableau de décision.',
  intro:
    'Deux annonces proches en prix ne représentent pas le même achat. La bonne comparaison remet la version, l’historique, les dépenses à venir et votre usage sur une base commune avant de donner du poids au coup de cœur.',
  image: '/assets/guides/comparer-deux-voitures-occasion.webp',
  imageAlt:
    'Deux voitures familiales d’occasion face à un dossier, une liste de contrôle et un mètre ruban',
  publishedAt: '2026-07-20',
  updatedAt: '2026-07-20',
  consultedAt: '20 juillet 2026',
  readingTime: '18 min',
  takeaways: [
    'Comparez les versions exactes et les preuves disponibles, pas seulement le modèle, l’année et le kilométrage.',
    'Ramenez les dépenses prévisibles sur votre durée de détention : pneus, entretien, énergie et revente estimée.',
    'Une voiture moins bien notée peut rester le bon choix si son compromis correspond mieux à votre priorité réelle.',
  ],
  sections: [
    {
      id: 'avant-tableau',
      title: 'Avant de noter : définir ce qui peut vraiment éliminer une voiture',
      content: (
        <>
          <p>
            Une comparaison utile commence avant les annonces. Je sépare les critères en trois niveaux : les
            non-négociables, les compromis acceptables et les préférences. Un besoin ISOFIX, une hauteur de garage
            ou une mensualité maximale n’a pas le même statut qu’une couleur ou qu’un toit ouvrant.
          </p>
          <p>
            Cette hiérarchie empêche une voiture séduisante de rester dans la course alors qu’elle échoue sur
            l’usage. Elle évite aussi l’excès inverse : éliminer une bonne piste pour une option agréable mais peu
            utilisée. Avant le premier appel, écrivez au maximum cinq non-négociables et donnez à chacun un moyen
            de vérification.
          </p>
          <div className="as-table-scroll" tabIndex={0} aria-label="Exemple de hiérarchie des critères automobiles">
            <table>
              <thead><tr><th>Niveau</th><th>Exemple</th><th>Conséquence</th></tr></thead>
              <tbody>
                <tr><td>Non-négociable</td><td>Deux sièges enfant installés sans condamner la place avant</td><td>Échec = voiture écartée</td></tr>
                <tr><td>Compromis</td><td>Coffre plus petit mais coffre de toit possible</td><td>Coût et contrainte à chiffrer</td></tr>
                <tr><td>Préférence</td><td>Couleur, jantes ou équipement de confort</td><td>Départage seulement après le reste</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Si vous hésitez encore sur la catégorie, commencez par le <a href="/outils/diagnostic-projet/">diagnostic
            de projet</a>. Le comparatif détaillé devient rentable lorsque deux ou trois pistes répondent déjà au
            cahier des charges.
          </p>
        </>
      ),
    },
    {
      id: 'base-comparable',
      title: '1. Mettre les deux annonces sur une base comparable',
      content: (
        <>
          <p>
            Je commence par identifier précisément génération, moteur, boîte, finition, date de première mise
            en circulation et équipements. Une année affichée dans le titre peut être l’année-modèle, pas la
            date d’immatriculation. Deux finitions au nom voisin peuvent aussi différer sur les aides à la
            conduite, les phares, les sièges ou la taille des roues.
          </p>
          <p>
            J’ajoute tous les frais nécessaires pour rendre la voiture prête à l’usage : carte grise, livraison,
            pneus à court terme, entretien imminent, deuxième clé et accessoires réellement nécessaires. Chez
            un professionnel, la DGCCRF rappelle que les prestations supplémentaires doivent être facultatives
            et détaillées lorsqu’elles ne sont pas incluses dans le prix annoncé.
          </p>
        </>
      ),
    },
    {
      id: 'version-options',
      title: 'Identifier la version exacte et les équipements qui changent vraiment l’achat',
      content: (
        <>
          <p>
            Les titres d’annonce simplifient parfois la version. Je rapproche les informations de la carte grise,
            du VIN lorsque son partage est possible, du catalogue de l’année et des photos. Une finition lancée
            après la première immatriculation annoncée, une puissance incohérente ou un équipement absent sur les
            images impose une question avant le déplacement.
          </p>
          <p>
            Toutes les options n’ont pas la même valeur. Certaines modifient l’usage ou le coût : taille des roues,
            attelage homologué, phares, pompe à chaleur sur une électrique, chargeur embarqué, sièges spécifiques ou
            aides à la conduite. D’autres sont surtout des préférences. Je chiffre d’abord ce qu’il faudrait ajouter
            après l’achat et ce qui ne peut pas être ajouté raisonnablement.
          </p>
          <ul className="as-article-checklist">
            <li>Demander une photo lisible de la carte grise en masquant les données personnelles inutiles.</li>
            <li>Comparer la puissance, l’énergie, la date et la variante avec la description.</li>
            <li>Faire confirmer par écrit les équipements décisifs, sans se fier uniquement au décodage automatique.</li>
            <li>Noter les roues et pneus réellement montés, pas seulement ceux de la brochure.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'preuves',
      title: '2. Noter la qualité des preuves avant l’état supposé',
      content: (
        <>
          <p>
            Une annonce promet « entretien à jour » ; un dossier montre les dates, kilométrages et opérations.
            Je donne plus de valeur aux éléments vérifiables : factures, procès-verbal de contrôle technique,
            lien HistoVec récent, rappels contrôlés avec le VIN et explication cohérente des réparations.
          </p>
          <aside className="as-article-note as-note-fact">
            <strong>Fait sourcé</strong>
            <p>
              HistoVec peut faire apparaître la première mise en circulation, les changements de titulaire,
              le statut administratif et certains sinistres ayant donné lieu à une procédure de réparation
              contrôlée. Le service ne fournit toutefois pas le détail des réparations et seul le titulaire
              actuel peut générer le rapport.
            </p>
          </aside>
          <p>
            Une absence de facture ne prouve pas un défaut. Elle augmente l’incertitude. Je la traite comme
            un risque à accepter, à faire vérifier ou à éviter, jamais comme une certitude mécanique.
          </p>
        </>
      ),
    },
    {
      id: 'premier-appel',
      title: 'Le premier appel doit réduire l’incertitude, pas négocier trop tôt',
      content: (
        <>
          <p>
            Je prépare les mêmes questions pour les deux vendeurs afin de ne pas favoriser inconsciemment la
            voiture préférée. L’objectif est de vérifier la cohérence de l’histoire et d’obtenir les documents
            avant de consacrer du temps à un déplacement. Une réponse imprécise n’est pas automatiquement un
            mensonge, mais elle doit rester notée comme une information non vérifiée.
          </p>
          <div className="as-article-warning">
            <strong>Trame d’appel courte</strong>
            <ul>
              <li>Depuis quand possédez-vous le véhicule et pourquoi le vendez-vous ?</li>
              <li>Quelles opérations ont été réalisées au cours des deux dernières années ?</li>
              <li>Quels frais sont à prévoir dans les douze prochains mois ?</li>
              <li>Y a-t-il eu réparation de carrosserie, sinistre ou remplacement d’un élément important ?</li>
              <li>Pouvez-vous transmettre le contrôle technique, les factures utiles et un lien HistoVec récent ?</li>
              <li>Le prix annoncé inclut-il tous les frais hors immatriculation ?</li>
            </ul>
          </div>
          <p>
            Chez un professionnel, les prestations annexes doivent être présentées et détaillées comme telles.
            La fiche de la DGCCRF rappelle aussi les documents à remettre et, pour un véhicule de plus de quatre
            ans, la remise du contrôle technique de moins de six mois avant la vente. Ces règles ne disent pas que
            la voiture est en parfait état : le contrôle est une source parmi d’autres et ne comporte aucun démontage.
          </p>
        </>
      ),
    },
    {
      id: 'cout',
      title: '3. Comparer le coût sur la durée, pas l’écart de prix du jour',
      content: (
        <>
          <p>
            Pour chaque voiture, je pose les mêmes hypothèses : kilomètres annuels, durée de détention, prix de
            l’énergie, entretien et pneus, assurance obtenue auprès de l’assureur, puis valeur de revente estimée.
            Je calcule une fourchette basse et haute lorsque la donnée est incertaine.
          </p>
          <div className="as-article-warning">
            <strong>À ne pas mélanger</strong>
            <ul>
              <li><b>Faits :</b> prix affiché, kilométrage, facture déjà produite.</li>
              <li><b>Estimations :</b> consommation réelle, entretien futur, valeur de revente.</li>
              <li><b>Hypothèses :</b> kilomètres et durée de détention choisis pour le calcul.</li>
              <li><b>Avis :</b> compromis que je préfère au regard du besoin.</li>
            </ul>
          </div>
          <p>
            Vous pouvez poser cette comparaison dans le <a href="/outils/comparateur-cout-global/">comparateur
            de coût global</a>. Le résultat reste une simulation à partir de vos données, pas une promesse de
            dépense ou de revente.
          </p>
        </>
      ),
    },
    {
      id: 'fourchettes',
      title: 'Construire un scénario central et un scénario défavorable',
      content: (
        <>
          <p>
            Un seul total donne une précision artificielle. Pour l’entretien, la consommation et la revente, je
            conserve au moins deux scénarios. Le scénario central reprend les informations disponibles et une
            utilisation normale. Le scénario défavorable réduit la revente estimée et ajoute les frais plausibles
            déjà proches : pneus, grosse révision, distribution selon le moteur ou remise à niveau documentée.
          </p>
          <p>
            Exemple de lecture : A coûte 2 000 € de moins dans le scénario central, mais son avantage disparaît
            si quatre pneus et un entretien important arrivent la première année. La conclusion n’est pas « A est
            moins chère », mais « A est moins chère seulement si ces deux dépenses sont déjà traitées ou intégrées
            au prix ». Cette formulation indique précisément ce qu’il faut vérifier ou négocier.
          </p>
          <div className="as-table-scroll" tabIndex={0} aria-label="Exemple de scénarios de coût pour deux occasions">
            <table>
              <thead><tr><th>Hypothèse</th><th>Voiture A</th><th>Voiture B</th></tr></thead>
              <tbody>
                <tr><td>Scénario central</td><td>Prix + frais déjà identifiés</td><td>Prix + frais déjà identifiés</td></tr>
                <tr><td>Scénario défavorable</td><td>Revente basse + dépenses proches</td><td>Revente basse + dépenses proches</td></tr>
                <tr><td>Élément qui renverse le choix</td><td>À écrire explicitement</td><td>À écrire explicitement</td></tr>
              </tbody>
            </table>
          </div>
        </>
      ),
    },
    {
      id: 'usage',
      title: '4. Faire gagner votre usage réel',
      content: (
        <>
          <p>
            Je transforme chaque priorité en test observable : siège enfant installé, poussette chargée,
            conducteur bien positionné, place de parking reproduite, portion d’autoroute parcourue, téléphone
            connecté. Un coffre annoncé à 500 litres ne dit pas si sa forme accepte votre matériel.
          </p>
          <p>
            Je distingue ensuite les critères non négociables des préférences. Une voiture qui échoue sur un
            non-négociable sort du tableau ; elle ne compense pas avec un toit ouvrant ou une couleur appréciée.
          </p>
        </>
      ),
    },
    {
      id: 'essai-comparable',
      title: 'Faire deux essais comparables plutôt que deux impressions différentes',
      content: (
        <>
          <p>
            Si possible, j’utilise le même parcours et la même grille : démarrage à froid, ville, route dégradée,
            voie rapide, manœuvre et stationnement. La radio reste coupée au début. Je teste ensuite les commandes,
            la position de conduite, la visibilité, le téléphone et les équipements décisifs. Les sensations sont
            notées juste après l’essai, avant de discuter longuement avec le vendeur.
          </p>
          <ol>
            <li><b>Avant de rouler :</b> pneus, voyants, niveaux visibles, documents et démarrage.</li>
            <li><b>À basse vitesse :</b> embrayage ou rampage, direction, bruits de train roulant et visibilité.</li>
            <li><b>Sur route :</b> freinage progressif, maintien de cap, boîte, reprise et confort.</li>
            <li><b>À l’arrêt :</b> climatisation, aides, caméras, ouvrants, charge et accessoires.</li>
            <li><b>Après :</b> nouvelle observation visuelle et liste des points à faire contrôler.</li>
          </ol>
          <p>
            Ce protocole ne constitue pas une inspection mécanique. Un bruit, une trace ou un comportement peut
            justifier l’intervention d’un professionnel qualifié avant l’achat ; l’absence de symptôme pendant
            l’essai ne garantit pas l’absence de défaut.
          </p>
        </>
      ),
    },
    {
      id: 'tableau',
      title: '5. Utiliser un tableau pondéré sans lui abandonner la décision',
      content: (
        <>
          <p>
            Je limite le tableau à cinq ou six familles : adéquation à l’usage, budget global, preuves et état,
            agrément, risque de dépenses, facilité de revente. Chaque famille reçoit un poids totalisant 100,
            défini avant l’essai pour éviter de modifier les règles en faveur de la voiture préférée.
          </p>
          <aside className="as-article-note as-note-opinion">
            <strong>Mon avis</strong>
            <p>
              Le score sert à révéler le prix d’un coup de cœur, pas à l’interdire. Si la voiture plaisir perd
              de quatre points mais que le compromis est compris et finançable, le choix peut rester assumé.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'exemple-pondere',
      title: 'Exemple de pondération : faire apparaître le vrai compromis',
      content: (
        <>
          <p>
            Pour une famille qui roule 18 000 km par an, je peux attribuer 30 points à l’usage, 25 au coût global,
            20 aux preuves et à l’état, 15 à l’agrément et 10 à la facilité de revente. Chaque voiture reçoit une
            note sur dix dans chaque famille, accompagnée d’une justification. La multiplication par le poids
            évite qu’un équipement spectaculaire compense artificiellement un défaut important.
          </p>
          <div className="as-table-scroll" tabIndex={0} aria-label="Exemple de tableau pondéré pour deux voitures d’occasion">
            <table>
              <thead><tr><th>Famille</th><th>Poids</th><th>Question à trancher</th></tr></thead>
              <tbody>
                <tr><td>Usage</td><td>30 %</td><td>La voiture simplifie-t-elle réellement la semaine type ?</td></tr>
                <tr><td>Coût global</td><td>25 %</td><td>L’écart reste-t-il acceptable dans le scénario défavorable ?</td></tr>
                <tr><td>Preuves et état</td><td>20 %</td><td>Les affirmations importantes sont-elles documentées ?</td></tr>
                <tr><td>Agrément</td><td>15 %</td><td>Le compromis reste-t-il plaisant sur les trajets fréquents ?</td></tr>
                <tr><td>Revente</td><td>10 %</td><td>La configuration sera-t-elle simple à expliquer, sans garantie de prix ?</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Les poids sont une hypothèse, pas une vérité. Ils doivent être choisis avant de noter les voitures.
            Si le score final contredit fortement votre envie, cherchez quelle ligne explique l’écart : c’est là
            que se trouve le compromis à assumer ou l’information à vérifier.
          </p>
        </>
      ),
    },
    {
      id: 'signaux-abandon',
      title: 'Préparer les motifs d’abandon avant le déplacement',
      content: (
        <>
          <p>
            L’argent et le temps déjà consacrés à une voiture ne la rendent pas meilleure. Je définis donc à
            l’avance les événements qui interrompent la démarche : identité ou documents incohérents, refus de
            transmettre les pièces essentielles, frais ajoutés au dernier moment, défaut important non annoncé,
            essai refusé sans raison claire ou pression pour verser immédiatement un acompte.
          </p>
          <p>
            Un signal isolé peut parfois s’expliquer. Plusieurs incohérences diminuent la qualité des preuves et
            doivent peser dans la comparaison. Le bon réflexe n’est pas de diagnostiquer à distance, mais de
            ralentir, obtenir une vérification indépendante ou revenir à l’autre piste.
          </p>
        </>
      ),
    },
    {
      id: 'decision',
      title: '6. Finir par une condition d’achat claire',
      content: (
        <>
          <p>
            La conclusion doit tenir en une phrase : « je choisis A si l’inspection confirme X et si le vendeur
            fournit Y ; sinon je reviens à B ». Cette condition évite qu’une négociation ou un déplacement déjà
            effectué transforme une incertitude en achat précipité.
          </p>
          <p>
            Pour l’examen d’un exemplaire, utilisez aussi la <a href="/conseils/verifier-voiture-occasion/">liste
            des vérifications avant achat</a>. Elle réduit les angles morts sans remplacer une inspection physique.
          </p>
          <p>
            Conservez enfin une page de synthèse avec le prix complet, les documents reçus, les points restant à
            vérifier et votre condition d’achat. Si une nouvelle annonce apparaît, elle devra battre cette base sur
            les critères importants ; vous évitez ainsi de recommencer toute la réflexion à chaque notification.
          </p>
        </>
      ),
    },
  ],
  sources: [
    {
      title: 'HistoVec — espace acheteur',
      publisher: 'Ministère de l’Intérieur',
      url: 'https://histovec.interieur.gouv.fr/histovec/acheteur',
    },
    {
      title: 'FAQ HistoVec — contenu et limites du rapport',
      publisher: 'Ministère de l’Intérieur',
      url: 'https://histovec.interieur.gouv.fr/histovec/faq-document-administratif',
    },
    {
      title: 'Les garanties légales de conformité et contre les vices cachés',
      publisher: 'DGCCRF — Ministère de l’Économie',
      url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/les-garanties-legales-de-conformite-et-contre-les-vices-caches',
    },
    {
      title: 'Achat d’un véhicule d’occasion : obligations du vendeur',
      publisher: 'DGCCRF — Ministère de l’Économie',
      url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/achat-dun-vehicule-doccasion-quelles-sont-les-obligations-du-vendeur',
    },
  ],
  faqs: [
    {
      question: 'Vaut-il mieux une voiture plus récente ou moins kilométrée ?',
      answer:
        'Aucun critère ne gagne seul. Il faut rapprocher l’usage passé, l’entretien, la version, les dépenses à venir et votre propre usage. Un faible kilométrage mal documenté n’est pas automatiquement préférable.',
    },
    {
      question: 'Combien de critères faut-il noter ?',
      answer:
        'Cinq ou six familles suffisent généralement. Un tableau trop détaillé crée une précision artificielle et dilue les critères réellement décisifs.',
    },
    {
      question: 'Un rapport HistoVec vierge garantit-il une voiture sans accident ?',
      answer:
        'Non. HistoVec constitue une source administrative utile, mais ne recense pas tous les événements ni le détail de toutes les réparations.',
    },
  ],
  relatedSlugs: ['verifier-voiture-occasion', 'quelle-citadine-15000-euros', 'quel-suv-30000-euros'],
};
